import React, {FC, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {getTimezonePreferences} from '../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {useLinkNavigation} from '../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {SettingsFooter} from '../../../../libs/shared/ui/src/components/settings/settingsFooter';
import {Skeleton} from '../../../../libs/shared/ui/src/components/utility/skeleton';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsWidgetsPageWidgetModel} from '../../../models/api/analyticsWidgetsPageModel';
import {SettingsSidebarInfoBox} from '../../../settings/common/settingsSidebarInfoBox';
import {getDefaultAnalyticsReportType} from '../../../store/panels/analytics/analyticsPanelSelectors';
import {setIsSidePanelOpenAction} from '../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {getIsSidePanelOpen} from '../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {getAnalyticsSelectedNamespace} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {AnalyticsParameters} from '../../../store/panels/analytics/parameters/analyticsParameters';
import {
  createAnalyticsDashboardAsyncAction,
  updateAnalyticsDashboardAsyncAction,
} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {makeGetAnalyticsWidgetsPage} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {makeAnalyticsReportTypeUrl} from '../../helpers/analyticsUrlHelpers';
import {ReportSidePanel} from '../reportSidePanel/reportSidePanel';
import {reportInnerContent} from '../reportViewer/reportCommons';
import {SupportReportConfigurationForm} from './supportReportConfigurationForm';
import {SupportReportConfigurationSidePanel} from './supportReportConfigurationSidePanel';

/*
 * Constants.
 */

const supportReportHelpCenterLink = 'https://help.front.com/en/articles/1273600';

/*
 * Props.
 */

interface SupportReportConfigurationProps {
  panelId: AnalyticsPanelId;
  parameters: AnalyticsParameters;
  isVisible: boolean;
}

/*
 * Style.
 */

const StyledReportViewerContentDiv = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column;
  overflow: hidden;
`;

const StyledReportHeaderWrapperDiv = styled.div`
  ${reportInnerContent};
  width: 100%;
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 20px;
  padding: 27px 48px 0 48px;
`;

const StyledReportTitleDiv = styled.div`
  flex: 1;
  margin-right: 8px;
  color: ${(p) => p.theme.greys.shade90};
  font-size: 24px;
  font-weight: ${fontWeights.black};
`;

const StyledContentWrapperDiv = styled.div`
  ${reportInnerContent}
  width: 100%;
  padding: 0 48px 0 48px;
  display: flex;
  flex-flow: row;
  gap: 32px;
  flex: 1;
  overflow-y: auto;
`;

const StyledSettingsSidebarInfoBoxWrapperDiv = styled.div`
  flex: 0 0 250px;
  position: sticky;
  top: 0;
`;

const StyledFooterWrapperDiv = styled.div`
  margin: 0 -48px;
  ${reportInnerContent}
  width: 100%;
`;

const StyledFormContainerDiv = styled.div`
  flex: 1;
`;

/*
 * Component.
 */

export const SupportReportConfiguration: FC<SupportReportConfigurationProps> = (props) => {
  const {panelId, isVisible, parameters} = props;
  const timezonePreference = useAppSelector(getTimezonePreferences);

  const getAnalyticsWidgetsPage = useMemo(() => makeGetAnalyticsWidgetsPage<void>(() => panelId), [panelId]);
  const analyticsWidgetsPage = useAppSelector(getAnalyticsWidgetsPage);
  const isLoading = !analyticsWidgetsPage;
  const isSidePanelOpen = useAppSelector(getIsSidePanelOpen);
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [reportName, setReportName] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState(timezonePreference.timezone);
  const [widgets, setWidgets] = useState<ReadonlyArray<AnalyticsWidgetsPageWidgetModel>>([]);
  const [selectedWidget, setSelectedWidget] = useState<AnalyticsWidgetsPageWidgetModel>();

  const isValid = reportName.length > 0 && widgets.length > 0;

  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const defaultReportType = useAppSelector(getDefaultAnalyticsReportType);

  useEffect(() => {
    if (isTeammateNamespace(namespace)) {
      // Redirect to default report if changing to a teammate or global namespace while on the page.
      dispatch(navigateToLinkAction(makeAnalyticsReportTypeUrl(defaultReportType, parameters)));
    }
  }, [defaultReportType, dispatch, namespace, parameters]);

  const onSaveAsync = async () => {
    setIsSaving(true);
    try {
      if (!analyticsWidgetsPage?.page) {
        await dispatch(
          createAnalyticsDashboardAsyncAction(panelId, {
            timezone: selectedTimezone,
            widgets,
            title: reportName,
          }),
        );
      } else {
        await dispatch(
          updateAnalyticsDashboardAsyncAction(panelId, {
            id: analyticsWidgetsPage.page.id,
            timezone: selectedTimezone,
            widgets,
            title: reportName,
          }),
        );
      }
    } finally {
      setIsDirty(false);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!analyticsWidgetsPage) {
      return;
    }
    const {page} = analyticsWidgetsPage;
    if (!page) {
      return;
    }
    setSelectedTimezone(page.timezone);
    setWidgets(page.widgets);
    setReportName(page.title);
  }, [analyticsWidgetsPage]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <StyledFormContainerDiv>
          <Skeleton height="28px" width="400px" />
        </StyledFormContainerDiv>
      );
    }
    return (
      <SupportReportConfigurationForm
        reportName={reportName}
        timezone={selectedTimezone}
        widgets={widgets}
        onTimezoneSelect={(tz) => {
          setSelectedTimezone(tz);
          setIsDirty(true);
        }}
        onReportNameChange={(name) => {
          setReportName(name);
          setIsDirty(true);
        }}
        moveWidget={(fromIndex, toIndex) => {
          setWidgets((existingWidgets) => {
            const newWidgets = [...existingWidgets];
            const [removedWidget] = newWidgets.splice(fromIndex, 1);
            newWidgets.splice(toIndex, 0, removedWidget);
            return newWidgets;
          });
          setIsDirty(true);
        }}
        onDeleteWidget={(index) => {
          setWidgets((existingWidgets) => existingWidgets.filter((_, i) => i !== index));
          setIsDirty(true);
        }}
        onEditWidget={(index) => {
          setSelectedWidget(widgets[index]);
          dispatch(setIsSidePanelOpenAction(true));
          setIsDirty(true);
        }}
      />
    );
  };

  return (
    <>
      <StyledReportViewerContentDiv>
        <StyledReportHeaderWrapperDiv>
          <StyledReportTitleDiv>
            <Trans id="analytics-automation-reports-support-report-header">Support Report</Trans>
          </StyledReportTitleDiv>
        </StyledReportHeaderWrapperDiv>
        <StyledContentWrapperDiv>
          {renderContent()}
          <StyledSettingsSidebarInfoBoxWrapperDiv>
            <SettingsSidebarInfoBox
              title={
                <Trans id="analytics-automation-reports-support-reports-infobox-title">Support Report</Trans>
              }
              body={
                <>
                  <p>
                    <Trans id="analytics-automation-reports-support-report-infobox-body-1">
                      Showcase your service quality by sharing metrics like response time or CSAT with
                      customers.
                    </Trans>
                  </p>
                  <p>
                    <Trans id="analytics-automation-reports-support-report-infobox-body-2">
                      Be sure to select views to limit metrics to specific inboxes or teammate groups.
                    </Trans>
                  </p>
                </>
              }
              linkFooter={
                <Trans id="analytics-automation-reports-support-report-infobox-footer">Learn More</Trans>
              }
              onLinkFooterClick={() => navigateToLink(supportReportHelpCenterLink)}
            />
          </StyledSettingsSidebarInfoBoxWrapperDiv>
        </StyledContentWrapperDiv>
        {isDirty && (
          <StyledFooterWrapperDiv>
            <SettingsFooter>
              <ButtonGroup>
                <ButtonGroupSpacer />
                <Button
                  isDisabled={isSaving}
                  onClick={() => {
                    if (analyticsWidgetsPage?.page) {
                      setSelectedTimezone(analyticsWidgetsPage.page.timezone);
                      setWidgets(analyticsWidgetsPage.page.widgets);
                      setReportName(analyticsWidgetsPage.page.title);
                    } else {
                      setSelectedTimezone(timezonePreference.timezone);
                      setWidgets([]);
                      setReportName('');
                    }
                    setIsDirty(false);
                  }}
                >
                  <Trans id="analytics-automation-reports-support-report-cancel-button">Cancel</Trans>
                </Button>
                <Button
                  variant={ButtonVariantsEnum.PRIMARY}
                  isDisabled={isSaving || !isValid}
                  onClick={onSaveAsync}
                >
                  <Trans id="analytics-automation-reports-support-report-save-button">Save</Trans>
                </Button>
              </ButtonGroup>
            </SettingsFooter>
          </StyledFooterWrapperDiv>
        )}
      </StyledReportViewerContentDiv>
      <ReportSidePanel
        isVisible={isVisible}
        renderCustomSidePanel={(onRequestClose) => {
          if (!isSidePanelOpen) {
            return null;
          }
          return (
            <SupportReportConfigurationSidePanel
              onRequestClose={() => {
                onRequestClose();
                setSelectedWidget(undefined);
              }}
              metric={selectedWidget}
              onSave={(widget) => {
                setIsDirty(true);
                if (selectedWidget) {
                  setWidgets((existingMetrics) =>
                    existingMetrics.map((m) => (m === selectedWidget ? widget : m)),
                  );
                  setSelectedWidget(undefined);
                } else {
                  setWidgets((existingWidgets) => [...existingWidgets, widget]);
                }
              }}
            />
          );
        }}
      />
    </>
  );
};
